/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import { TAB_VARIANTS, TAB_TYPES } from "../../../utility/constants";

/**
 * Turns PascalCase string into Title Case
 * @param {String} s
 */
export const pascalToTitleCase = (s) => {
	return (
		s &&
		s
			.replace(/([A-Z][a-z])/g, " $1")
			.replace(/([A-Z][A-Z]+)/g, " $1")
			.trim()
	);
};

/**
 * Turns PascalCase or camelCase string into Sentence case
 * @param {String} s
 */
export const toSentenceCase = (s) => {
	return (
		s &&
		s
			.replace(/([A-Z][a-z])/g, (v) => ` ${v.toLowerCase()}`)
			.replace(/([A-Z][A-Z]+)/g, " $1")
			.trim()
			.replace(/^./, (str) => {
				return str.toUpperCase();
			})
	);
};

/**
 * Check to make sure tab should show
 * @param {Object} contact The current contact object
 * @param {Object} tab The tab to check
 * @returns True if the ta should show and false if not
 */
export const tabShouldShow = (contact, tab) => {
	const activeTab = contact && tab.queues?.length ? tab.queues?.find((x) => x.Name === contact.queue) : true;
	return (
		(tab.type === TAB_TYPES.attribute && contact && activeTab) ||
		(tab.variant !== TAB_VARIANTS.static && contact && activeTab) ||
		tab.variant === TAB_VARIANTS.static
	);
};
/**
 * Turns a camelCase string into Title Case
 * @param {String} s
 */
export const camelToTitleCase = (s) => {
	return (
		s &&
		s
			.replace(/([A-Z][a-z])/g, " $1")
			.replace(/([A-Z][A-Z]+)/g, " $1")
			.trim()
			.replace(/^./, (str) => {
				return str.toUpperCase();
			})
	);
};

/**
 * Tries to parse a Json from a given string. If unsuccessful then it will return
 * an object with the original string and the error message
 * @param s json string
 * @param defaultVal optional default value to return in case of error
 */
export const tryJsonParse = (s, defaultVal) => {
	try {
		return JSON.parse(s);
	} catch (error) {
		return defaultVal !== undefined
			? defaultVal
			: {
					error: error.message,
					originalValue: s,
			  };
	}
};

/**
 * Helper to check if a string includes another string.
 * Can optionally check case sensitive or insensitive
 * @param str string to search within
 * @param search search term
 * @param caseSensitive should the search be case sensitive, optional, defaults to false
 */
export const includes = (str, search, caseSensitive = false) =>
	caseSensitive ? str.includes(search) : str.toLowerCase().includes(search.toLowerCase());

/**
 * opens a url in another tab
 * @param url A url string
 */
export const openLinkInNewTab = (url) => {
	if (typeof url === "string") {
		window.open(url);
	}
};

/**
 * This method takes in a string and uses a regex to check whether or not that string is a valid URL.
 * @param url The string to check if it is a valid url.
 */
export const checkIfUrl = (url) => {
	var regex = new RegExp(
		"(https?://(?:www.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9].[^s]{2,}|www.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9].[^s]{2,}|https?://(?:www.|(?!www))[a-zA-Z0-9]+.[^s]{2,}|www.[a-zA-Z0-9]+.[^s]{2,})",
	);
	return url?.match(regex) || false;
};

/**
 * Checks common strings and numbers that can represent a boolean and returns true/false
 * @param {String | Number} val value to convert
 */
export const valToBool = (val) => {
	if (typeof val === "string") {
		val = val.trim().toLowerCase();
	}

	switch (val) {
		case true:
		case "true":
		case 1:
		case "1":
		case "on":
		case "yes":
			return true;
		default:
			return false;
	}
};

/**
 * checks if object is empty
 * @param {Object} obj object to test
 */
export const isEmptyObject = (obj) => !obj || Object.keys(obj).length === 0;

/**
 * Converts seconds to hours minutes seconds, any value with 0 will be omitted
 * Examples:
 * 3661 => 1 hr 1 min 1 sec
 * 120 => 2 min
 * 3600 => 1 hr
 * @param {number} s seconds
 */
export const secondsToHms = (s) =>
	Object.entries({
		hr: ((s - (s % 3600)) / 3600) % 60,
		min: ((s - (s % 60)) / 60) % 60,
		sec: s % 60,
	})
		.reduce((res, [key, val]) => (val ? `${res} ${val} ${key}` : res), "")
		.trim() || "0 sec";

/**
 * Calculates the average from an array of numbers, rounded to at most 2 decimals
 * @param {Array} numbers
 */
export const avg = (numbers) => Math.round((numbers.reduce((a, b) => a + b) / numbers.length) * 100) / 100;
